import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  ErrorNotice,
  Field,
  LoadingState,
  PageHeading,
  Panel,
  TextInput,
} from '@bcecloud/portal-ui';
import { getJson, sendJson, errorMessage } from '@/lib/api';

/** System-settings keys for the portal-wide concierge defaults. */
const EMAIL_SETTING = 'portal_concierge_email';
const PHONE_SETTING = 'portal_concierge_phone';

/** One keyed system setting, as the settings API returns it. */
interface SettingValue {
  readonly name: string;
  readonly value: unknown;
}

/** Reads a keyed setting as a string, treating anything else as unset. */
function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Portal-wide defaults for the client portals — currently the concierge
 * contact every tenant falls back to when it has no override of its own.
 * Per-client overrides live on each organization's Settings tab.
 *
 * @returns The portal defaults page.
 */
export function PortalDefaultsPage(): React.ReactElement {
  const defaults = useQuery({
    queryKey: ['portal-defaults'],
    queryFn: async ({ signal }) => {
      const [email, phone] = await Promise.all([
        getJson<SettingValue>(`/settings/${EMAIL_SETTING}`, undefined, signal),
        getJson<SettingValue>(`/settings/${PHONE_SETTING}`, undefined, signal),
      ]);
      return { email: asString(email.value), phone: asString(phone.value) };
    },
  });

  if (defaults.isPending) return <LoadingState label="Loading portal defaults…" />;
  if (defaults.isError) {
    return (
      <ErrorNotice title="Portal defaults unavailable" message={errorMessage(defaults.error)} />
    );
  }

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="CLIENT PORTALS"
        title="Portal defaults"
        subtitle="Settings every client portal inherits unless an organization has its own override."
      />

      <DefaultsForm key={`${defaults.data.email}|${defaults.data.phone}`} loaded={defaults.data} />
    </section>
  );
}

/**
 * The defaults form, mounted per loaded value so fields initialize from data.
 *
 * @param props - The loaded default values.
 * @returns The form.
 */
function DefaultsForm({
  loaded,
}: {
  readonly loaded: { readonly email: string; readonly phone: string };
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState(loaded.email);
  const [phone, setPhone] = useState(loaded.phone);

  const save = useMutation({
    mutationFn: () =>
      sendJson('post', '/settings', {
        settings: [
          { name: EMAIL_SETTING, value: email.trim() },
          { name: PHONE_SETTING, value: phone.trim() },
        ],
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal-defaults'] }),
  });

  const dirty = email.trim() !== loaded.email || phone.trim() !== loaded.phone;

  return (
    <Panel className="mt-6 max-w-[640px] p-6">
      <h2 className="text-portal-section">Concierge routing</h2>
      <p className="mt-1 text-sm text-admin-muted">
        Where concierge questions are emailed, and the support phone shown on every client&rsquo;s
        help page. A client-specific override on an organization&rsquo;s Settings tab wins over
        these.
      </p>

      <form
        className="mt-5 space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (dirty && !save.isPending) save.mutate();
        }}
      >
        <Field label="Default concierge email" htmlFor="default-concierge-email">
          <TextInput
            id="default-concierge-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="concierge@bestcorporateevents.com"
          />
        </Field>

        <Field label="Default concierge phone" htmlFor="default-concierge-phone">
          <TextInput
            id="default-concierge-phone"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="(800) 849-8326"
          />
        </Field>

        {save.isError ? (
          <ErrorNotice inline title="Could not save" message={errorMessage(save.error)} />
        ) : null}
        {save.isSuccess && !dirty ? (
          <p role="status" className="text-sm text-emerald-700">
            Saved.
          </p>
        ) : null}

        <Button type="submit" disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save defaults'}
        </Button>
      </form>
    </Panel>
  );
}
